import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Grid,
  GridItem,
  Icon,
  Link,
  Divider,
  Flex
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { 
  FaQuestionCircle,
  FaLifeRing,
  FaEnvelope,
  FaWhatsapp,
  FaInstagram,
  FaShieldAlt
} from 'react-icons/fa';
import FAQSection from '../components/FAQSection';

// Add Google Fonts for luxury typography
const fontImport = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');
`;

// Inject the font styles
if (typeof document !== 'undefined' && !document.head.querySelector('[data-faq-page-fonts]')) {
  const styleElement = document.createElement('style');
  styleElement.textContent = fontImport;
  styleElement.setAttribute('data-faq-page-fonts', 'true');
  document.head.appendChild(styleElement);
}

const MotionBox = motion(Box);
const MotionGrid = motion(Grid);

const FAQPage = () => {
  const supportOptions = [
    {
      icon: FaEnvelope,
      title: 'Email Support',
      description: 'Get detailed help via email',
      contact: 'agnir.info@gmail.com',
      href: 'mailto:agnir.info@gmail.com',
      color: '#4285f4',
      availability: 'Response within 24 hours'
    },
    {
      icon: FaWhatsapp,
      title: 'WhatsApp Chat',
      description: 'Quick support via WhatsApp',
      contact: '+91 7387657127',
      href: 'https://wa.me/917387657127',
      color: '#25D366',
      availability: 'Available 9 AM - 6 PM'
    },
    {
      icon: FaInstagram,
      title: 'Social Media',
      description: 'Follow us for updates',
      contact: '@agnir.co',
      href: 'https://www.instagram.com/agnir.co?igsh=MW5mYmJiZ2VnYjlyYg==',
      color: '#E4405F',
      availability: 'Daily updates & stories'
    }
  ];

  const features = [
    {
      icon: FaShieldAlt,
      title: '6-Month Warranty',
      description: 'All jewelry comes with comprehensive warranty coverage'
    },
    {
      icon: FaLifeRing,
      title: '24/7 Online Support',
      description: 'Round-the-clock assistance for all your queries'
    },
    {
      icon: FaQuestionCircle,
      title: 'Easy Returns',
      description: 'Hassle-free returns and exchanges within 48 hours'
    }
  ];

  return (
    <Box 
      minH="100vh" 
      bgImage={`url(${process.env.REACT_APP_BG_URL})`}
      py={{ base: 10, lg: 20 }}
    >
      <Container maxW="container.xl">
        {/* Hero Section */}
        <MotionBox
          textAlign="center"
          mb={{ base: 12, lg: 16 }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <HStack justify="center" mb={6}>
            <Icon 
              as={FaQuestionCircle} 
              boxSize={{ base: 8, lg: 10 }} 
              color="gray.600" 
            />
          </HStack>
          <Heading
            as="h1"
            size={{ base: "2xl", lg: "3xl" }}
            fontFamily="'Playfair Display', serif"
            fontWeight="700"
            color="gray.800"
            mb={6}
            lineHeight="1.2"
          >
            Frequently Asked Questions
          </Heading>
          <Text
            fontSize={{ base: "lg", lg: "xl" }}
            color="gray.600"
            fontFamily="'Inter', sans-serif"
            maxW="700px"
            mx="auto"
            lineHeight="1.7"
            mb={8}
          >
            Everything you need to know about AGNIR jewelry. From materials and care instructions 
            to ordering and warranty information — we've got you covered.
          </Text>

          {/* Feature Highlights */}
          <MotionGrid
            templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
            gap={{ base: 6, md: 8 }}
            maxW="900px"
            mx="auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {features.map((feature, index) => (
              <MotionBox
                key={feature.title}
                bg="white"
                p={{ base: 6, md: 8 }}
                borderRadius="xl"
                textAlign="center"
                boxShadow="0 4px 20px rgba(0, 0, 0, 0.08)"
                border="1px solid"
                borderColor="gray.100"
                whileHover={{
                  y: -5,
                  boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)"
                }}
                transition={{ duration: 0.3 }}
              >
                <Icon
                  as={feature.icon}
                  boxSize={8}
                  color="gray.600"
                  mb={4}
                />
                <Heading
                  as="h3"
                  size="md"
                  fontFamily="'Playfair Display', serif"
                  fontWeight="600"
                  color="gray.800"
                  mb={2}
                >
                  {feature.title}
                </Heading>
                <Text
                  fontSize="sm"
                  color="gray.600"
                  fontFamily="'Inter', sans-serif"
                  lineHeight="1.5"
                >
                  {feature.description}
                </Text>
              </MotionBox>
            ))}
          </MotionGrid>
        </MotionBox>

        {/* FAQ Section */}
        <MotionBox
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          mb={{ base: 12, lg: 16 }}
        >
          <FAQSection showTitle={false} maxWidth="container.lg" spacing={6} />
        </MotionBox>

        {/* Support Section */}
        <MotionBox
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <Box
            bg="white"
            borderRadius="2xl"
            p={{ base: 8, lg: 12 }}
            boxShadow="0 10px 30px rgba(0, 0, 0, 0.1)"
            border="1px solid"
            borderColor="gray.100"
          >
            <VStack spacing={{ base: 8, lg: 10 }}>
              <Box textAlign="center">
                <Heading
                  as="h2"
                  size={{ base: "xl", lg: "2xl" }}
                  fontFamily="'Playfair Display', serif"
                  fontWeight="600"
                  color="gray.800"
                  mb={4}
                >
                  Need More Help?
                </Heading>
                <Text
                  fontSize={{ base: "md", lg: "lg" }}
                  color="gray.600"
                  fontFamily="'Inter', sans-serif"
                  maxW="600px"
                  mx="auto"
                  lineHeight="1.6"
                >
                  Our customer support team is always ready to assist you. 
                  Choose your preferred way to get in touch with us.
                </Text>
              </Box>

              <Divider borderColor="gray.200" />

              <Grid
                templateColumns={{ base: '1fr', lg: 'repeat(3, 1fr)' }}
                gap={{ base: 8, lg: 10 }}
                w="full"
              >
                {supportOptions.map((option, index) => (
                  <MotionBox
                    key={option.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 * index }}
                  >
                    <Link
                      href={option.href}
                      isExternal
                      _hover={{ textDecoration: 'none' }}
                    >
                      <VStack
                        spacing={4}
                        p={{ base: 6, lg: 8 }}
                        bg="gray.50"
                        borderRadius="xl"
                        border="1px solid"
                        borderColor="gray.200"
                        textAlign="center"
                        _hover={{
                          bg: "gray.100",
                          transform: "translateY(-3px)",
                          boxShadow: "0 6px 20px rgba(0, 0, 0, 0.1)"
                        }}
                        transition="all 0.3s ease"
                        minH="240px"
                        justify="center"
                      >
                        <Box
                          p={4}
                          borderRadius="full"
                          bg="white"
                          boxShadow="0 2px 10px rgba(0, 0, 0, 0.1)"
                        >
                          <Icon
                            as={option.icon}
                            boxSize={8}
                            color={option.color}
                          />
                        </Box>

                        <VStack spacing={2}>
                          <Heading
                            as="h3"
                            size="md"
                            fontFamily="'Playfair Display', serif"
                            fontWeight="600"
                            color="gray.800"
                          >
                            {option.title}
                          </Heading>
                          <Text
                            fontSize="sm"
                            color="gray.600"
                            fontFamily="'Inter', sans-serif"
                            lineHeight="1.5"
                          >
                            {option.description}
                          </Text>
                        </VStack>

                        <VStack spacing={1}>
                          <Text
                            fontFamily="'Inter', sans-serif"
                            fontWeight="600"
                            color="gray.800"
                            fontSize="md"
                          >
                            {option.contact}
                          </Text>
                          <Text
                            fontSize="xs"
                            color="gray.500"
                            fontFamily="'Inter', sans-serif"
                          >
                            {option.availability}
                          </Text>
                        </VStack>
                      </VStack>
                    </Link>
                  </MotionBox>
                ))}
              </Grid>
            </VStack>
          </Box>
        </MotionBox>

        {/* Bottom CTA */}
        <MotionBox
          textAlign="center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <Flex
            direction={{ base: 'column', md: 'row' }}
            align="center"
            justify="center"
            gap={{ base: 4, md: 8 }}
            bg="linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)"
            color="white"
            p={{ base: 8, lg: 12 }}
            borderRadius="2xl"
            boxShadow="0 10px 30px rgba(0, 0, 0, 0.2)"
          >
            <VStack spacing={2} align={{ base: 'center', md: 'start' }} flex={1}>
              <Heading
                as="h3"
                size="lg"
                fontFamily="'Playfair Display', serif"
                fontWeight="600"
              >
                Still have questions?
              </Heading>
              <Text
                fontFamily="'Inter', sans-serif"
                opacity={0.9}
                fontSize="lg"
              >
                Our team is here to help you find the perfect piece.
              </Text>
            </VStack>
            <HStack spacing={4}>
              <MotionBox
                as="a"
                href="/contact"
                bg="white"
                color="gray.800"
                px={8}
                py={4}
                borderRadius="xl"
                fontFamily="'Inter', sans-serif"
                fontWeight="600"
                fontSize="lg"
                textDecoration="none"
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 6px 20px rgba(255, 255, 255, 0.3)"
                }}
                whileTap={{ scale: 0.95 }}
                _hover={{ textDecoration: "none" }}
              >
                Contact Us
              </MotionBox>
            </HStack>
          </Flex>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default FAQPage;